import React, { useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { line, curveMonotoneX } from 'd3-shape';
import {
  formatCompanyName,
  formatGeoChartDayLabel,
  formatGeoChartDayTooltipTitle,
  shouldShowGeoChartDayTick,
} from '../utils/geoPresentation';
import { getGeoBrandColor } from '../utils/geoBrandColors';
import GeoMetricTitle from './GeoMetricTitle';

const CHART_WIDTH = 994;
const CHART_HEIGHT = 400;
const PAD = { top: 20, right: 32, bottom: 40, left: 44 };
const DOT_RADIUS = 3;
const DOT_RADIUS_HOVER = 5;
/** Keep in sync with `.geo-insights-line-chart__clip` animation in components.css */
const LINE_CLIP = { delay: 0.18, duration: 1.05, dotLead: 0.1 };

function getDotAnimationDelay(pointIndex, pointCount, seriesIndex) {
  const progress = pointCount > 1 ? pointIndex / (pointCount - 1) : 0;
  const clipReach = LINE_CLIP.delay + progress * LINE_CLIP.duration;
  const lead = LINE_CLIP.dotLead + seriesIndex * 0.025;
  return Math.max(0, clipReach - lead);
}

function getSeriesPoints(values, maxY, plotW, plotH) {
  const stepX = values.length > 1 ? plotW / (values.length - 1) : plotW;
  return values.map((value, index) => ({
    x: PAD.left + index * stepX,
    y: PAD.top + plotH - (value / maxY) * plotH,
    value,
  }));
}

function getDayStep(plotW, labelCount) {
  return labelCount > 1 ? plotW / (labelCount - 1) : plotW;
}

function getDayCenterX(index, plotW, labelCount) {
  const stepX = getDayStep(plotW, labelCount);
  return PAD.left + index * stepX;
}

/** Hover band between midpoints of neighboring days. */
function getHoverBand(index, labelCount, plotW) {
  if (labelCount <= 1) {
    return { x: PAD.left, width: plotW };
  }
  const step = getDayStep(plotW, labelCount);
  const left = index === 0
    ? PAD.left
    : PAD.left + (index - 0.5) * step;
  const right = index === labelCount - 1
    ? PAD.left + plotW
    : PAD.left + (index + 0.5) * step;
  return { x: left, width: right - left };
}

/** Monotone cubic spline — smooth curves without loops or backward bends (time-series safe). */
function buildLinePath(values, maxY, plotW, plotH) {
  const points = getSeriesPoints(values, maxY, plotW, plotH);
  if (points.length === 0) return '';
  if (points.length === 1) {
    return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  }

  const path = line()
    .x((d) => d.x)
    .y((d) => d.y)
    .curve(curveMonotoneX)(points);

  return path ?? '';
}

export default function GeoBrandCoverageChart({ labels = [], series = [], loading = false }) {
  const plotW = CHART_WIDTH - PAD.left - PAD.right;
  const plotH = CHART_HEIGHT - PAD.top - PAD.bottom;
  const clipPathId = useId().replace(/:/g, '');
  const [hoveredDay, setHoveredDay] = useState(null);

  const { maxY, hasData, seriesGraphics } = useMemo(() => {
    const allValues = series.flatMap((s) => s.values || []);
    const nextMaxY = Math.max(10, ...allValues, 1);
    const nextHasData = allValues.some((v) => v > 0);
    const nextGraphics = series.map((item, seriesIndex) => ({
      ...item,
      color: getGeoBrandColor(seriesIndex),
      d: buildLinePath(item.values || [], nextMaxY, plotW, plotH),
      points: getSeriesPoints(item.values || [], nextMaxY, plotW, plotH),
    }));
    return { maxY: nextMaxY, hasData: nextHasData, seriesGraphics: nextGraphics };
  }, [series, plotW, plotH]);

  const tooltipRows = useMemo(() => {
    if (hoveredDay == null) return [];
    const dayIndex = hoveredDay.index;
    if (!labels[dayIndex]) return [];
    return series
      .map((item, seriesIndex) => ({
        brand: item.brand,
        color: getGeoBrandColor(seriesIndex),
        value: Number(item.values?.[dayIndex]) || 0,
        isClient: Boolean(item.isClient),
      }))
      .sort((a, b) => b.value - a.value || a.brand.localeCompare(b.brand));
  }, [hoveredDay, labels, series]);

  const yTicks = [0, Math.round(maxY / 2), maxY];

  const handleDayHover = (index, event) => {
    setHoveredDay({
      index,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const clearDayHover = () => setHoveredDay(null);

  const cursorX = hoveredDay != null
    ? getDayCenterX(hoveredDay.index, plotW, labels.length)
    : null;

  return (
    <div className="report-card volume-chart-panel geo-insights-chart-card">
      <div className="chart-panel__header">
        <GeoMetricTitle metricKey="mention_share_over_time">Mention Share Over Time</GeoMetricTitle>
      </div>
      <div className="chart-panel__body">
        {loading ? (
          <div className="insights-chart-area insights-chart-area--md">Loading chart…</div>
        ) : !hasData ? (
          <div className="geo-insights-empty">
            <div className="geo-insights-empty__icon" aria-hidden="true">📦</div>
            <p>No data to display</p>
          </div>
        ) : (
          <>
            <div className="product-depth-legend geo-insights-chart-legend geo-insights-chart-legend--top">
              {series.map((item, seriesIndex) => (
                <div key={item.brand} className="product-depth-legend__item">
                  <span
                    className="product-depth-legend__swatch"
                    style={{ background: getGeoBrandColor(seriesIndex) }}
                    aria-hidden="true"
                  />
                  <span className="product-depth-legend__label">{formatCompanyName(item.brand, 22)}</span>
                </div>
              ))}
            </div>
            <div
              className="geo-insights-line-chart-wrap"
              style={{
                '--geo-line-clip-delay': `${LINE_CLIP.delay}s`,
                '--geo-line-clip-duration': `${LINE_CLIP.duration}s`,
              }}
              onMouseLeave={clearDayHover}
            >
              <svg
                className="geo-insights-line-chart"
                viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label="Mention share over time"
              >
                <defs>
                  <clipPath id={clipPathId}>
                    <rect
                      x={PAD.left}
                      y={PAD.top - 8}
                      width={plotW}
                      height={plotH + 16}
                      className="geo-insights-line-chart__clip"
                    />
                  </clipPath>
                </defs>
                <line
                  x1={PAD.left}
                  y1={PAD.top}
                  x2={PAD.left}
                  y2={PAD.top + plotH}
                  className="geo-insights-line-chart__axis"
                />
                {yTicks.map((tick) => {
                  const y = PAD.top + plotH - (tick / maxY) * plotH;
                  return (
                    <g key={tick}>
                      <line
                        x1={PAD.left}
                        y1={y}
                        x2={CHART_WIDTH - PAD.right}
                        y2={y}
                        className="geo-insights-line-chart__grid"
                      />
                      <text x={PAD.left - 8} y={y + 4} textAnchor="end" className="geo-insights-line-chart__tick chart-axis-tick-value--svg">
                        {tick}%
                      </text>
                    </g>
                  );
                })}
                {seriesGraphics.map((item, seriesIndex) => (
                  <g
                    key={item.brand}
                    className="geo-insights-line-chart__series"
                    style={{ '--series-index': seriesIndex }}
                  >
                    <path
                      d={item.d}
                      fill="none"
                      stroke={item.color}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="geo-insights-line-chart__line"
                      clipPath={`url(#${clipPathId})`}
                    />
                    {item.points.map((point, pointIndex) => {
                      const isHovered = hoveredDay?.index === pointIndex;
                      const pointCount = item.points.length;
                      return (
                        <circle
                          key={`${item.brand}-${pointIndex}`}
                          cx={point.x}
                          cy={point.y}
                          r={isHovered ? DOT_RADIUS_HOVER : DOT_RADIUS}
                          className={`geo-insights-line-chart__dot${isHovered ? ' geo-insights-line-chart__dot--hovered' : ''}`}
                          style={{
                            stroke: item.color,
                            '--point-delay': `${getDotAnimationDelay(pointIndex, pointCount, seriesIndex)}s`,
                          }}
                        />
                      );
                    })}
                  </g>
                ))}
                {cursorX != null ? (
                  <line
                    x1={cursorX}
                    y1={PAD.top}
                    x2={cursorX}
                    y2={PAD.top + plotH}
                    className="geo-insights-line-chart__cursor"
                    pointerEvents="none"
                  />
                ) : null}
                {labels.map((label, index) => {
                  const band = getHoverBand(index, labels.length, plotW);
                  return (
                    <rect
                      key={`hover-${label}`}
                      x={band.x}
                      y={PAD.top}
                      width={band.width}
                      height={plotH}
                      fill="transparent"
                      className="geo-insights-line-chart__hit"
                      onMouseEnter={(event) => handleDayHover(index, event)}
                      onMouseMove={(event) => handleDayHover(index, event)}
                    />
                  );
                })}
                {labels.map((label, index) => {
                  if (!shouldShowGeoChartDayTick(index, labels.length)) return null;
                  const x = getDayCenterX(index, plotW, labels.length);
                  return (
                    <text
                      key={label}
                      x={x}
                      y={CHART_HEIGHT - 10}
                      textAnchor="middle"
                      className="geo-insights-line-chart__tick geo-insights-line-chart__tick--x chart-axis-label-text--svg"
                    >
                      {formatGeoChartDayLabel(label)}
                    </text>
                  );
                })}
              </svg>
            </div>
            {hoveredDay != null && labels[hoveredDay.index] && createPortal(
              <div
                className="map-hover-tooltip geo-line-chart-tooltip"
                style={{ left: hoveredDay.x, top: hoveredDay.y }}
                role="tooltip"
              >
                <div className="geo-line-chart-tooltip__date">
                  {formatGeoChartDayTooltipTitle(labels[hoveredDay.index])}
                </div>
                {tooltipRows.length === 0 ? (
                  <p className="geo-line-chart-tooltip__empty">No mention share recorded</p>
                ) : (
                  <ul className="geo-line-chart-tooltip__list">
                    {tooltipRows.map((row) => (
                      <li key={row.brand} className="geo-line-chart-tooltip__row">
                        <span
                          className="geo-line-chart-tooltip__swatch"
                          style={{ background: row.color }}
                          aria-hidden="true"
                        />
                        <span className="geo-line-chart-tooltip__brand">
                          {formatCompanyName(row.brand, 28)}
                          {row.isClient ? ' (You)' : ''}
                        </span>
                        <span className="geo-line-chart-tooltip__value">
                          {row.value.toFixed(1)}
                          %
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>,
              document.body,
            )}
          </>
        )}
      </div>
    </div>
  );
}
